"use client";

import clsx from "clsx";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";

import { Body } from "@shared/ui/typography/body";

import s from "./selectable-badge.module.scss";

export type SelectableBadgeKind = "filter" | "tag";

type CommonProps = {
  className?: string;
  selected?: boolean;
  disabled?: boolean;
  kind?: SelectableBadgeKind;
  children: ReactNode;
};

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  as?: "button";
};

type SpanProps = Omit<HTMLAttributes<HTMLSpanElement>, "children"> & {
  as: "span";
};

export type SelectableBadgeProps = CommonProps & (ButtonProps | SpanProps);

export const SelectableBadge = (props: SelectableBadgeProps) => {
  const {
    className,
    selected = false,
    kind = "filter",
    children,
    disabled,
    ...rest
  } = props;

  const as = "as" in rest ? rest.as : "button";

  const labelSize = kind === "tag" ? "uppercase-small" : "uppercase";

  return (
    <>
      {as === "button" ? (
        <button
          className={clsx(s.root, s.clickable, s[kind], className, {
            [s.selected]: selected,
            [s.disabled]: disabled,
          })}
          aria-pressed={selected}
          type={"type" in rest ? rest.type : "button"}
          {...rest}
        >
          <Body size={labelSize} weight="semiBold" className={s.label}>
            {children}
          </Body>
        </button>
      ) : (
        <span
          className={clsx(s.root, s[kind], className, {
            [s.selected]: selected,
          })}
          {...rest}
        >
          <Body size={labelSize} weight="semiBold" className={s.label}>
            {children}
          </Body>
        </span>
      )}
    </>
  );
};

SelectableBadge.displayName = "SelectableBadge";
